import React from 'react';
import LegalDocLayout from '../../components/ChinaDolphins/LegalDocLayout/LegalDocLayout';

const TOC = [
  { id: 'purpose-scope', label: '1. Purpose and Scope' },
  { id: 'regulatory-framework', label: '2. Legal and Regulatory Framework' },
  { id: 'prohibited-goods', label: '3. Prohibited and Restricted Goods' },
  { id: 'financial-integrity', label: '4. Financial Integrity, AML & CTF' },
  { id: 'stakeholder-commitments', label: '5. Stakeholder Commitments' },
  { id: 'violations-enforcement', label: '6. Violations and Legal Reporting' },
];

export const TradeAmlComplianceContent = () => (
  <div>
    <p>
      <strong>ChinaDolphins</strong> Anti-Money Laundering (AML), Counter-Terrorist Financing (CTF), and Global Trade Compliance Policy.
    </p>
    <p>
      As an international B2B platform facilitating cross-border commerce between global buyers and Chinese manufacturers, ChinaDolphins maintains rigorous protocols to prevent financial crime, illicit trade, sanctions circumvention, and the movement of unlawful goods.
    </p>

    <h2 id="purpose-scope">1. Purpose and Scope</h2>
    <p>
      This policy applies universally to all Buyers, Sourcing Agents, and Manufacturing Factories. It governs all financial flows, product classifications, customs documentation, and transaction settlement workflows across the platform.
    </p>

    <h2 id="regulatory-framework">2. Legal and Regulatory Framework</h2>
    <p>
      ChinaDolphins aligns its compliance and screening procedures with leading domestic and international regulatory regimes:
    </p>
    <ul>
      <li><strong>Hong Kong:</strong> Anti-Money Laundering and Counter-Terrorist Financing Ordinance (Cap. 615), Import and Export Ordinance (Cap. 60), and United Nations Sanctions Ordinances.</li>
      <li><strong>Mainland China:</strong> Anti-Money Laundering Law of the PRC, Customs Law, and Export Control Law of the PRC.</li>
      <li><strong>International Standards:</strong> Recommendations of the Financial Action Task Force (FATF), OFAC/EU sanctions frameworks, and international export-control treaties.</li>
    </ul>

    <h2 id="prohibited-goods">3. Prohibited and Restricted Goods</h2>
    <p>
      The platform strictly prohibits listing, sourcing, quoting, or shipping the following categories:
    </p>
    <h3>3.1 Weapons &amp; Military Hardware</h3>
    <p>
      Firearms, ammunition, military-grade ordnance, restricted weapon parts, tactical attachments, or technical production blueprints.
    </p>
    <h3>3.2 Explosives &amp; Hazardous Substances</h3>
    <p>
      Explosives, chemical precursor materials, highly toxic compounds, or unlawfully traded hazardous chemicals.
    </p>
    <h3>3.3 Controlled Dual-Use Items</h3>
    <p>
      Equipment, software, or specialized materials classified under multilateral export-control regimes without verified government licenses.
    </p>
    <h3>3.4 Contraband &amp; Counterfeit Merchandise</h3>
    <p>
      Counterfeit branded goods, pirated intellectual property, narcotics, protected wildlife specimens, or items seized by customs.
    </p>

    <h2 id="financial-integrity">4. Financial Integrity, AML &amp; CTF</h2>
    <h3>4.1 Mandatory Corporate Commercial Account Match</h3>
    <p>
      All inbound Buyer payments must originate exclusively from a corporate bank or verified commercial payment account matching the exact verified legal company name of the registered Buyer. Mismatched remitters, personal unverified accounts, or unauthorized third-party remitter payments are strictly prohibited and will be automatically flagged for compliance review or rejected.
    </p>
    <h3>4.2 Real-Time Payment Monitoring</h3>
    <p>
      Transactions are audited for suspicious velocity, irregular jurisdiction routing, or anomalous amounts.
    </p>
    <h3>4.3 Anti-Structuring Protocols</h3>
    <p>
      The following patterns are subject to automatic compliance reviews and source-of-funds verifications:
    </p>
    <ul>
      <li>Fragmented, repetitive micro-payments designed to evade reporting thresholds.</li>
      <li>Unexplained third-party remitter payments without commercial justification.</li>
      <li>Requests to redirect settlement balances to unverified offshore bank accounts.</li>
    </ul>

    <h2 id="stakeholder-commitments">5. Stakeholder Commitments</h2>
    <ul>
      <li><strong>The Platform:</strong> Performs KYC/KYB screening, verifies business licenses, monitors trade logs, and cooperates with lawful authorities.</li>
      <li><strong>Manufacturing Factories:</strong> Ensure complete and accurate customs declarations and never conceal contraband within legitimate production runs.</li>
      <li><strong>Sourcing Agents:</strong> Support on-the-ground factory inspections and immediately flag suspicious production lines or unverified exporters.</li>
      <li><strong>Global Buyers:</strong> Commit legitimate commercial funds from matched corporate accounts, provide accurate corporate identity data, and act as lawful Importers of Record.</li>
    </ul>

    <h2 id="violations-enforcement">6. Violations and Legal Reporting</h2>
    <p>
      Confirmed breaches of trade compliance or anti-money-laundering mandates result in permanent account bans, transaction suspension or account review where legally required, and formal referrals to financial intelligence units and customs authorities.
    </p>
    <div style={{ background: '#eff6ff', padding: '16px', borderRadius: '8px', border: '1px solid #bfdbfe', marginTop: '16px' }}>
      <p style={{ margin: 0, fontWeight: 600, color: '#1e40af' }}>
        Mandatory Registration Agreement: &quot;I agree to the ChinaDolphins Global Trade and AML Compliance Policy.&quot;
      </p>
    </div>
  </div>
);

const TradeAmlCompliancePage = () => {
  return (
    <LegalDocLayout
      title="Global Trade & AML Compliance Policy"
      subtitle="Anti-Money Laundering, Counter-Terrorist Financing, and international sanctions enforcement standards."
      effectiveDate="January 1, 2026"
      lastUpdated="August 13, 2026"
      jurisdiction="Hong Kong / International (MTN GLOBAL LIMITED)"
      tocItems={TOC}
    >
      <TradeAmlComplianceContent />
    </LegalDocLayout>
  );
};

export default TradeAmlCompliancePage;
